import Route from "@mapmycustomers/shared/types/entity/Route";
import { isNotEmpty, isNotEmptyString } from "@mapmycustomers/shared/util/assert";

import getRemainingRouteStops from "@app/util/route/getRemainingRouteStops";

/**
 * Whether the solve persisted on a route still describes the drive the scene is about to
 * render — i.e. whether the route can be opened without buying a fresh preview.
 *
 * Most of the invalidation is the BE's: it clears polyline, both totals and `legMetrics`
 * together whenever the stop list, the travel basis or the start time changes, and it declines
 * to project a leg array whose length no longer fits the stop list. So presence of all of them
 * is the currency signal, and no client-side timestamp comparison is needed.
 *
 * A checked-off stop is the one case the BE cannot see, because marking a stop done changes
 * neither the stop list nor the travel basis. The stored array distinguishes the two ways a
 * route can arrive in that state, without needing a flag to say so:
 *
 *   - Solved with every stop active, then a stop checked off. That stop was in the solve, so it
 *     still carries travel metrics — while the totals now describe more driving than the scene
 *     renders. Stale, and the leftover metrics are what give it away.
 *   - Solved after the check-off (the Re-build button). The preview covers only the remaining
 *     stops, so the array we store pads the checked-off ones to `{}` and the totals describe
 *     exactly the remaining drive. Current, and reusable.
 *
 * So a checked-off stop must be *bare* and a remaining stop must be *populated*. Metrics reach a
 * stop only by the BE projecting `legMetrics` onto it — there is no per-stop travel column and no
 * other writer — so an empty leg reliably reads back as absent keys rather than a stale value.
 */
const isStoredRoutePreviewCurrent = (route: Route): boolean => {
  const { routeDetail, stopTime } = route;
  if (
    !isNotEmptyString(routeDetail.polyline) ||
    !isNotEmpty(routeDetail.totalDistance) ||
    !isNotEmpty(routeDetail.totalDuration)
  ) {
    return false;
  }
  if (!stopTime?.length || !getRemainingRouteStops(route).length) {
    return false;
  }
  return stopTime.every((stop) =>
    stop.checkedOff
      ? !isNotEmpty(stop.travelTime) && !isNotEmpty(stop.travelDistance)
      : isNotEmpty(stop.travelTime) && isNotEmpty(stop.travelDistance)
  );
};

export default isStoredRoutePreviewCurrent;
